import jhttp from "jhttp";
import CryptoJS from "crypto-js";
import { thirdServiceUsed } from "@sunzi/utils";
import i18n from "./i18n";
import { ApiResponse } from "@/sunzi";
import { loadBase64FromURL, createObjectURL } from "@/utils";
import {
  meituAppKey,
  meituSecretKey,
  meituTimeout,
  aliTimeout,
  aliAppCode,
  versaTimeout,
  myFacutHost,
  imageCartoonHost,
  textToSpeechHost,
  faceppConfig,
  cutoutApiKey,
  cutoutHost,
} from "@/config";
import { getToken7n, dateTime, uuid, upload7nB64, base64ToBlob } from ".";

/** 迈远人脸抠图token */
const _myFaceCutToken = async (): Promise<string> => {
  const _decrypt = (value: string) => {
    const SECRETKEY = CryptoJS.enc.Latin1.parse("1234567891111117");
    const bytes = CryptoJS.AES.decrypt(value.replace(/%2B/g, "+"), SECRETKEY, {
      iv: CryptoJS.enc.Utf8.parse("0000000000000000"),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return bytes.toString(CryptoJS.enc.Utf8);
  };
  // 先判断token是否过期
  let token = localStorage.getItem("_sunzi_myfacut_token");
  const expire = localStorage.getItem("_sunzi_myfacut_token_expire");
  if (token && expire && Date.now() < parseInt(expire))
    // 没有过期
    return _decrypt(token);
  else {
    const response: any = await jhttp.post("https://auth.crop.myuxc.com/auth");
    if (response.code === 0) {
      localStorage.setItem("_sunzi_myfacut_token", response.token);
      localStorage.setItem(
        "_sunzi_myfacut_token_expire",
        (Date.now() + 14000 * 60).toString(),
      );
      return _decrypt(response.token);
    } else {
      throw Error("get token error");
    }
  }
};

/** 迈远人脸抠图 */
export const myFaceCut = async (
  base64: string,
): Promise<ApiResponse<string[]>> => {
  try {
    // 获取token
    const token = await _myFaceCutToken();
    // 转文件流
    const blob = await base64ToBlob(base64);
    const filename = `${uuid()}.${blob.type.split("/")[1]}`;
    const file = new File([blob], filename, { type: blob.type });
    let formdata = new FormData();
    formdata.append("image", file);
    const response: any = await fetch(
      `${myFacutHost}/matting/api/v10/present`,
      {
        method: "POST",
        body: formdata,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ).then((response) => {
      if (response.status >= 400) {
        throw new Error("NETWORK ERROR");
      }
      return response.json();
    });
    if (response.code === 0 && response.data.length > 0) {
      // 加载图片
      const data = await Promise.all<string>(
        response.data.map((item: string) =>
          loadBase64FromURL(`${myFacutHost}/static/${item}`, 600, "png"),
        ),
      );
      return {
        status: true,
        data,
      };
    } else throw Error();
  } catch (e) {
    if (e.message === "Failed to fetch" || e.message === "NETWORK ERROR") {
      // 网络错误
      return {
        status: false,
        message: e.message,
      };
    } else {
      // 处理错误信息
      return {
        status: false,
        message: i18n.format("utils.meitu.error.other"),
      };
    }
  }
};

/** 美图人脸抠图 */
export const meituFaceCut = async (
  base64: string,
): Promise<ApiResponse<string>> => {
  try {
    const baseArray = base64.split(",");
    const response: any = await Promise.race([
      jhttp.json(
        `https://openapi.mtlab.meitu.com/v1/headreplace?api_key=${meituAppKey}&api_secret=${meituSecretKey}`,
        {
          parameter: {
            rsp_media_type: "base64",
            nType: 0,
          },
          media_info_list: [
            {
              media_data: baseArray[1],
              media_profiles: {
                media_data_type: "jpg",
              },
            },
          ],
        },
      ),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("Network Timeout")), meituTimeout),
      ),
    ]);
    if (response.media_info_list) {
      return {
        status: true,
        data: `${baseArray[0]},${response.media_info_list[0].media_data}`,
      };
    }
    switch (response.ErrorCode) {
      case 20001:
        return {
          status: false,
          message: i18n.format("utils.meitu.error.20001"),
        };
      case 20003:
        return {
          status: false,
          message: i18n.format("utils.meitu.error.20003"),
        };
      case 20004:
        return {
          status: false,
          message: i18n.format("utils.meitu.error.20004"),
        };
      case 20005:
        return {
          status: false,
          message: i18n.format("utils.meitu.error.20005"),
        };
      default:
        return {
          status: false,
          message: i18n.format("utils.meitu.error.other"),
        };
    }
  } catch (e) {
    // 处理错误信息
    return {
      status: false,
      message: i18n.format("utils.meitu.error.other"),
    };
  }
};

/** 阿里影刻 */
export const aliSketch = async (
  base64: string,
): Promise<ApiResponse<string>> => {
  try {
    const baseArray = base64.split(",");
    const response: any = await Promise.race([
      jhttp.json(
        "https://carve.market.alicloudapi.com/effect/carve",
        {
          type: "jpg",
          photo: baseArray[1],
        },
        { Authorization: `APPCODE ${aliAppCode}` },
      ),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("Network Timeout")), aliTimeout),
      ),
    ]);
    if (response.status === 0)
      return {
        status: true,
        data: response.data.result,
      };
    else
      return {
        status: false,
      };
  } catch (e) {
    // 处理错误信息
    return {
      status: false,
    };
  }
};

/** 马卡龙实例分割 */
export const versaSegment = async (
  source: string,
): Promise<ApiResponse<string[]>> => {
  // 上传到七牛
  const token = await getToken7n("sunzi-ai-versa");
  const encodeKey = `versa/segment/${dateTime}/${uuid()}.jpg`;
  const { key } = await upload7nB64({
    base64: source,
    token,
    key: encodeKey,
    bucket: host7n["upload-z0"],
  });
  // 调用实例分割
  try {
    const response: any = await Promise.race([
      jhttp.request(`${host7n["prefix-z0"]}${key}?mkr_seg_ins/9,13,16`, {
        method: "GET",
      }),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("Network Timeout")), versaTimeout),
      ),
    ]);
    if (response.result && response.result.length > 0) {
      return {
        status: true,
        data: await Promise.all(
          response.result
            .map((item: any) => item.target)
            .reduce((a: any, b: any) => a.concat(b))
            .map((item: any) => item.url),
          /*.map(async (item: string) => {
            const { image } = await getTrimImage(item);
            return image;
          })*/
        ),
      };
    } else {
      return {
        status: false,
      };
    }
  } catch (e) {
    // 处理错误信息
    return {
      status: false,
    };
  }
};

/** 马卡龙风格迁移 */
export const versaStyleTransfer = async (
  path: string,
  trans: number,
): Promise<ApiResponse<string>> => {
  try {
    const response: any = await Promise.race([
      jhttp.request(
        `${path}?mkr_style_trans/${[, "s00", "s0"][trans.toString().length]}${trans}`,
        { method: "GET" },
      ),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("Network Timeout")), versaTimeout),
      ),
    ]);
    if (response.target_url) {
      return {
        status: true,
        data: response.target_url,
      };
    } else {
      return {
        status: false,
      };
    }
  } catch (e) {
    // 处理错误信息
    return {
      status: false,
    };
  }
};

/** 图片卡通化 */
export const imageCartoon = (
  source: string,
  effectType: number = 1,
): Promise<ApiResponse<string>> => {
  const data = new FormData();
  data.append("effectType", effectType.toString());
  data.append("file", base64ToBlob(source));
  return fetch(imageCartoonHost, {
    body: data,
    method: "POST",
  })
    .then((data) => data.json())
    .then((result) => ({
      status: true,
      data: result?.imgPath || "",
    }))
    .catch(() => ({
      status: false,
    }));
};

/** 语音转文字 */
export const textToSpeech = async (
  text: string,
  voiceName: string,
  languageCode: string,
  speakingRate: string = "1",
  pitch: string = "1",
): Promise<ApiResponse<string>> => {
  try {
    const response = await jhttp.post(textToSpeechHost, {
      text: text,
      voiceName: voiceName,
      speakingRate: speakingRate,
      pitch: pitch,
      languageCode: languageCode,
    });
    if (response.error === 0) {
      return {
        status: true,
        message: response.message,
        data: response.mp3,
      };
    } else {
      return {
        status: false,
        message: response.message,
      };
    }
  } catch (e) {
    return {
      status: false,
    };
  }
};

/** 人脸识别 */
export const faceRecognition = async (
  source: string | Blob | File,
  landmark?: boolean,
): Promise<
  ApiResponse<
    {
      rect: {
        left: number;
        top: number;
        width: number;
        height: number;
      };
      angle: number;
      isBlur: boolean;
      landmark?: any;
    }[]
  >
> => {
  try {
    const formData: FormData = new FormData();
    formData.append("api_key", faceppConfig.apiKey);
    formData.append("api_secret", faceppConfig.apiSecret);
    formData.append("return_attributes", "headpose,blur");
    if (landmark) {
      formData.append("return_landmark", "1");
    }
    const type = Object.prototype.toString.call(source);
    if (type === "[object String]") {
      formData.append("image_base64", source);
    } else if (type === "[object Blob]" || type === "[object File]") {
      formData.append("image_file", source);
    }

    const response = await fetch(`${faceppConfig.baseUrl}/facepp/v3/detect`, {
      method: "POST",
      body: formData,
    }).then((response) => {
      if (response.status > 400) {
        throw new Error();
      }
      return response.json();
    });

    if (!response.error_message) {
      const result = response.faces.map((item: any) => {
        const { blurness, gaussianblur, motionblur } = item.attributes.blur;
        let isBlur;
        if (
          blurness.value === motionblur.value &&
          gaussianblur.value === motionblur.value
        ) {
          // 模糊数据一致
          if (blurness.value > blurness.threshold) {
            isBlur = true;
          } else {
            isBlur = false;
          }
        } else if (
          blurness.value > blurness.threshold ||
          motionblur.value > motionblur.threshold ||
          gaussianblur.value > gaussianblur.threshold
        ) {
          isBlur = true;
        } else if (
          blurness.value < blurness.threshold ||
          motionblur.value < motionblur.threshold ||
          gaussianblur.value < gaussianblur.threshold
        ) {
          isBlur = false;
        }

        return {
          rect: item.face_rectangle,
          angle: item.attributes.headpose.roll_angle,
          isBlur: isBlur,
          landmark: item.landmark,
        };
      });
      return {
        status: true,
        data: result,
      };
    } else {
      throw Error();
    }
  } catch (e) {
    return {
      status: false,
    };
  } finally {
  }
};

/** cutout站点 实例分割 */
export const cutoutVersaSegment = async (
  source: string,
  fileType: string = "jpg",
  toBlob?: boolean,
  factCut?: boolean,
): Promise<ApiResponse<string | Blob>> => {
  const file = base64ToBlob(source);
  const formData = new FormData();
  formData.append("file", file, `${uuid()}.${fileType}`);
  // 调用实例分割
  try {
    let host = cutoutHost;
    if (factCut) {
      host = `${cutoutHost}?mattingType=3&crop=true`;
    }
    const blob: any = await Promise.race([
      fetch(host, {
        method: "POST",
        headers: { APIKEY: cutoutApiKey },
        body: formData,
      }).then((res) => {
        const type = res.headers.get("Content-Type");
        if (type?.indexOf("image") !== -1) return res.blob();
        else return res.json();
      }),
      new Promise((resolve, reject) =>
        setTimeout(
          () => reject(new Error("Network Timeout")),
          versaTimeout * 2,
        ),
      ),
    ]);

    if (blob && blob instanceof Blob) {
      if (toBlob) {
        return {
          status: true,
          data: blob,
        };
      } else {
        return {
          status: true,
          data: createObjectURL(blob),
        };
      }
    } else {
      return {
        status: false,
      };
    }
  } catch (e) {
    // 处理错误信息
    return {
      status: false,
      message: e.message,
    };
  }
};
